/**
 * DirtyGuardModal — "You have unsaved changes. Leave without saving?"
 *
 * Used in two contexts:
 *   1. Inside Modal.jsx — guards X button / Escape / backdrop when form is dirty
 *      Pass: open={confirmingClose} onConfirm={...} onCancel={...}
 *
 *   2. Inside CrudForm.jsx — guards Cancel button AND React Router navigation
 *      Pass: open={cancelPending} onConfirm={...} onCancel={...} blocker={blocker}
 *      When blocker.state === 'blocked', blocker.proceed() / blocker.reset() are called
 *      instead of the callback props.
 */
export default function DirtyGuardModal({ open, onConfirm, onCancel, blocker }) {
  const visible = open || blocker?.state === 'blocked';
  if (!visible) return null;

  const handleConfirm = () => {
    if (blocker?.state === 'blocked') blocker.proceed();
    else onConfirm?.();
  };

  const handleCancel = () => {
    if (blocker?.state === 'blocked') blocker.reset();
    else onCancel?.();
  };

  return (
    /* stopPropagation prevents overlay-click from bubbling to the parent Modal's overlay */
    <div style={overlay} onClick={(e) => { e.stopPropagation(); handleCancel(); }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleCancel(); } }}>
      <div style={dialog} onClick={(e) => e.stopPropagation()} role="button" tabIndex={0} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && e.stopPropagation()}>
        <p style={message}>You have unsaved changes. Leave without saving?</p>
        <div style={btnRow}>
          <button onClick={handleCancel} style={stayBtn}>No, stay</button>
          <button onClick={handleConfirm} style={leaveBtn}>Yes, leave</button>
        </div>
      </div>
    </div>
  );
}

/* ── Styles ─────────────────────────────────────────────────────────────────── */

const overlay = {
  position:       'fixed',
  inset:          0,
  background:     'rgba(6,39,34,0.72)',
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  zIndex:         1100,   /* above Modal's 1000 */
  backdropFilter: 'blur(1px)',
};

const dialog = {
  background: 'var(--color-bg-secondary)',
  borderRadius: '0.625rem',
  padding:      '1.75rem 2rem',
  width:        '100%',
  maxWidth:     '380px',
  boxShadow:    '0 16px 48px rgba(6,39,34,0.45)',
  border:       '1px solid var(--color-border)',
};

const message = {
  margin:      '0 0 1.25rem',
  fontSize:    '0.9375rem',
  fontWeight:  600,
  color: 'var(--color-text-primary)',
  lineHeight:  1.45,
  textAlign:   'center',
};

const btnRow = {
  display:        'flex',
  gap:            '0.75rem',
  justifyContent: 'center',
};

const stayBtn = {
  padding:      '0.5rem 1.5rem',
  border:       '1px solid var(--color-text-secondary)',
  borderRadius: '0.375rem',
  background:   'transparent',
  cursor:       'pointer',
  fontSize:     '0.875rem',
  color:        'var(--color-text-secondary)',
  fontWeight:   600,
};

const leaveBtn = {
  padding:      '0.5rem 1.5rem',
  border:       'none',
  borderRadius: '0.375rem',
  background:   'var(--color-danger)',
  color: 'var(--color-accent-btn-text)',
  cursor:       'pointer',
  fontSize:     '0.875rem',
  fontWeight:   700,
};
